"use client";

import { useState } from "react";
import type { Accept } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { PopoverContainerContext } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { PyloMedia } from "@/services/pylo-media.service";
import { MediaBrowser } from "./media-browser";

interface MediaPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** False for single-value relations — picking replaces the current choice. */
  multiple?: boolean;
  /** A relation's `media_allowed_computed_mime_types`; empty means unrestricted. */
  allowedMimeTypes?: Array<string>;
  /** Scopes what an upload made from inside the picker accepts. */
  accept?: Accept;
  title?: string;
  onConfirm: (files: Array<PyloMedia>) => void;
}

export const MediaPickerDialog = ({
  open,
  onOpenChange,
  multiple = true,
  allowedMimeTypes,
  accept,
  title = "Select a file",
  onConfirm,
}: MediaPickerDialogProps) => {
  // The toolbar's popovers and selects portal into this node, not <body>, so they
  // stay interactive and scrollable inside the modal — see PopoverContainerContext.
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedFiles, setSelectedFiles] = useState<Array<PyloMedia>>([]);

  const close = () => {
    onOpenChange(false);
    setSelectedIds(new Set());
    setSelectedFiles([]);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent
        ref={setPortalContainer}
        // No `overflow-hidden`: popovers portal into this node, so clipping here would
        // cut off a dropdown near the dialog edge. Each section owns its own scroll.
        className="flex h-[80vh] max-w-5xl flex-col gap-0 p-0"
      >
        <PopoverContainerContext.Provider value={portalContainer}>
          <DialogHeader className="border-b px-4 py-3">
            <DialogTitle className="text-[15px]">{title}</DialogTitle>
            <DialogDescription className="text-[12px]">
              {allowedMimeTypes && allowedMimeTypes.length > 0
                ? "Only file types this field accepts are listed. Drop a file here to upload it."
                : "Browse, filter, or drop a file here to upload it."}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1">
            <MediaBrowser
              mode="pick"
              compact
              multiple={multiple}
              allowedMimeTypes={allowedMimeTypes}
              accept={accept}
              selectedIds={selectedIds}
              onSelectionChange={(ids, files) => {
                setSelectedIds(ids);
                setSelectedFiles(files);
              }}
            />
          </div>

          <div className="flex items-center gap-2 border-t px-4 py-3">
            <span className="text-[12px] text-muted-foreground">{selectedIds.size} selected</span>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" className="h-8 text-[13px]" onClick={close}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-8 text-[13px]"
                disabled={selectedFiles.length === 0}
                onClick={() => {
                  onConfirm(selectedFiles);
                  close();
                }}
              >
                {multiple && selectedFiles.length > 1
                  ? `Select ${selectedFiles.length} files`
                  : "Select"}
              </Button>
            </div>
          </div>
        </PopoverContainerContext.Provider>
      </DialogContent>
    </Dialog>
  );
};
